import { Edit } from "@mui/icons-material";
import {
    Avatar,
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    IconButton,
    ImageList,
    ImageListItem,
    ImageListItemBar,
    Stack,
    Tooltip,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { CropDialog } from "../ImageCropDialog";

export const ImageGallery = ({
    images,
    setImages,
    form,
    croppedFile,
    setCroppedFile,
    product,
}) => {
    const {
        control,
        setValue,
        formState: { errors },
    } = form;
    const [selectAll, setSelectAll] = useState(false);

    const [originalImage, setOriginalImage] = useState(null); // data URL

    const [openCropper, setOpenCropper] = useState(false);

    useEffect(() => {
        setSelectAll(images?.length > 0 && images.every((img) => img.selected));
        setValue(
            "gallery",
            images?.filter(({ selected }) => selected).map((image) => image),
            { shouldValidate: true }
        );
    }, [images, setValue]);

    const handleImageChange = (idx, isChecked) => {
        const updated = [...images];
        updated[idx].selected = isChecked;
        setImages(updated);
    };

    const handleOpen = async (event) => {
        if (event.target.files.length) {
            console.log(event.target.files[0]);
            setOriginalImage(URL.createObjectURL(event.target.files[0]));
            setOpenCropper(true);
        }
    };

    const handleSelectAllCheck = (check) => {
        setImages((prev) =>
            prev.map((item, i) => ({
                ...item,
                selected: check,
            }))
        );
        // setSelectAll(true);
    };

    const handleImageSelectChange = async (url) => {
        const urlObj = new URL(url);
        const isHMImage =
            urlObj.hostname === "image.hm.com" ||
            urlObj.hostname === "d2h50zujfkj84t.cloudfront.net";
        const baseURL = process.env.REACT_APP_BASE_URL;

        const finalImageUrl = isHMImage
            ? `${baseURL}/web/proxy-image/?url=${encodeURIComponent(url)}`
            : url;

        setOriginalImage(finalImageUrl);
        setOpenCropper(true);
    };

    const handleCropDone = (file) => {
        setCroppedFile(file);
    };

    return (
        <Box>
            <Box>
                <FormLabel>Image & Gallery</FormLabel>
            </Box>

            {croppedFile && (
                <Box my={2}>
                    <FormLabel>Image</FormLabel>
                    <Box
                        sx={{
                            position: "relative",
                            width: 150,
                            aspectRatio: "3/4", // works in modern browsers
                            borderRadius: 3,
                            overflow: "hidden",
                            mt: 2,
                        }}
                    >
                        <Avatar
                            variant="square"
                            src={
                                croppedFile instanceof Blob
                                    ? URL.createObjectURL(croppedFile)
                                    : croppedFile || ""
                            }
                            sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                objectFit: "cover", // or "fill"
                            }}
                        />
                    </Box>
                </Box>
            )}
            <Button variant="contained" component="label">
                {!croppedFile ? "Upload " : "Change "}image
                <input
                    hidden
                    accept="image/*"
                    id="file-upload"
                    type="file"
                    {...form.register("file", {
                        required: {value:images.length===0,message:"Image required"}
                    })}
                    onChange={handleOpen}
                />
            </Button>
            <FormHelperText error={Boolean(errors.file)}>
                Upload image with aspect ratio 3:4 min size of 210*280, <br />{" "}
                You can use our cropper to crop a image
            </FormHelperText>
            {errors.file && (
                <FormHelperText error>{errors.file?.message}</FormHelperText>
            )}
            {/* hide the gallery if scraper not present */}
            {images?.length > 0 && (
                <>
                    <Stack
                        direction={"row"}
                        justifyContent={"space-between"}
                        alignItems={"center"}
                    >
                        <FormLabel>Gallery</FormLabel>
                        <FormControlLabel
                            label={`${selectAll ? "Deselect" : "Select"} All`}
                            control={
                                <Checkbox
                                    checked={selectAll}
                                    onChange={(e) =>
                                        handleSelectAllCheck(e.target.checked)
                                    }
                                />
                            }
                        />
                    </Stack>

                    <Box
                        mt={1}
                        mb={3}
                        sx={{
                            border: "1px solid",
                            borderColor: "rgba(0, 0, 0, 0.23)", // Default MUI Input border color
                            borderRadius: 1, // similar to default MUI input
                            // padding: 1,
                        }}
                    >
                        <FormControl
                            sx={{ mx: 1 }}
                            error={Boolean(errors.image)}
                            variant="standard"
                        >
                            <FormHelperText>
                                {errors.image && errors.image?.message}
                            </FormHelperText>

                            <ImageList
                                sx={{
                                    maxHeight: 450,
                                    scrollbarWidth: "none", // Firefox
                                    "&::-webkit-scrollbar": {
                                        display: "none", // Chrome, Safari, Edge
                                    },
                                }}
                                cols={3}
                                gap={5}
                            >
                                {images?.map?.((img, i) => (
                                    <ImageListItem key={`images-${i}`}>
                                        <img
                                            alt=""
                                            loading="lazy"
                                            srcSet={`${img.image}?w=164&h=164&fit=crop&auto=format&dpr=2 2x`}
                                            src={`${img.image}?w=164&h=164&fit=crop&auto=format`}
                                            style={{ borderRadius: 8 }}
                                        />
                                        <ImageListItemBar
                                            sx={{
                                                background:
                                                    "linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, " +
                                                    "rgba(0,0,0,0.5) 50%, rgba(0,0,0,0) 100%)",
                                                borderTopLeftRadius: 8,
                                                borderTopRightRadius: 8,
                                                "& .MuiImageListItemBar-titleWrap":
                                                    {
                                                        p: 0, // Removes padding
                                                    },
                                            }}
                                            title={
                                                <Tooltip
                                                    placement={"top"}
                                                    componentsProps={{
                                                        tooltip: {
                                                            sx: {
                                                                bgcolor:
                                                                    "white",
                                                                color: "black",
                                                                boxShadow: 2,
                                                                fontSize: 14,
                                                            },
                                                        },
                                                        arrow: {
                                                            sx: {
                                                                color: "white", // This sets the arrow color to match the tooltip background
                                                            },
                                                        },
                                                    }}
                                                    arrow
                                                    title={
                                                        "Use As Product Image"
                                                    }
                                                >
                                                    <IconButton
                                                        sx={{ color: "white" }}
                                                        onClick={
                                                            () =>
                                                                handleImageSelectChange(
                                                                    img.image
                                                                )

                                                            // handleOpen(img.image)
                                                        }
                                                    >
                                                        <Edit />
                                                    </IconButton>
                                                </Tooltip>
                                            }
                                            position={"top"}
                                            actionIcon={
                                                <Controller
                                                    name="gallery"
                                                    control={control}
                                                    defaultValue={[]}
                                                    render={({
                                                        field: {
                                                            value,
                                                            onChange,
                                                        },
                                                    }) => (
                                                        <Checkbox
                                                            onChange={(e) => {
                                                                handleImageChange(
                                                                    i,
                                                                    e.target
                                                                        .checked
                                                                );
                                                            }}
                                                            checked={
                                                                img.selected
                                                            }
                                                            sx={{
                                                                color: "white",
                                                                "&.Mui-checked":
                                                                    {
                                                                        color: "white",
                                                                    },
                                                            }}
                                                        />
                                                    )}
                                                />
                                            }
                                            actionPosition="right"
                                        />
                                    </ImageListItem>
                                ))}
                            </ImageList>
                        </FormControl>
                    </Box>
                </>
            )}
            <CropDialog
                open={openCropper}
                imageSrc={originalImage}
                onClose={() => setOpenCropper(false)}
                onCropDone={handleCropDone}
            />
        </Box>
    );
};
